<template>
  <b-card v-if="!!option" id="page-permission-form" no-body class="mb-0">
    <b-card-header>
      <b-card-title
        ><h2>
          {{ $t(getTitle()) }} {{ $t("pages.permissions.title").toLowerCase() }}
        </h2></b-card-title
      >
      <b-button variant="primary" @click="goBack">
        <feather-icon icon="ArrowLeftIcon" />
        {{ $t("back") }}
      </b-button>
    </b-card-header>

    <b-card-body>
      <validation-observer ref="simpleRules">
        <b-form>
          <b-row>
            <b-col cols="12">
              <b-form-group :label="$t(label('name'))" label-for="name">
                <validation-provider
                  #default="{ errors }"
                  :name="$t(label('name'))"
                  rules="required"
                >
                  <b-form-input
                    id="name"
                    v-model="permission.name"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="$t(label('name'))"
                    :disabled="!isSaveMode"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group
                :label="$t(label('idCatModule'))"
                label-for="idCatModule"
              >
                <v-select
                  ref="day"
                  v-model="permission.idCatModule"
                  input-id="day"
                  :options="listCatModulesPermissions"
                  label="name"
                  :placeholder="$t(label('idCatModule'))"
                  :reduce="(option) => option.id"
                >
                  <span slot="no-options">{{ $t("sorryOption") }}</span>
                </v-select>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group
                :label="$t(label('idCatTypeProfile'))"
                label-for="idCatType"
              >
                <v-select
                  ref="idCatType"
                  v-model="permission.idCatType"
                  input-id="idCatType"
                  :options="listCatTypeProfiles"
                  label="name"
                  :placeholder="$t(label('idCatTypeProfile'))"
                  :reduce="(option) => option.id"
                >
                  <span slot="no-options">{{ $t("sorryOption") }}</span>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col v-if="needsCompanySelection" cols="12">
              <b-alert show variant="warning" class="mb-2">
                <div class="d-flex align-items-center">
                  <feather-icon icon="AlertTriangleIcon" size="18" />
                  <span class="ml-1">
                    {{ $t("pages.permissions.form.selectClientCompany") }}
                  </span>
                </div>
              </b-alert>
            </b-col>
            <b-col cols="12" md="3">
              <validation-provider
                #default="{ errors }"
                :name="$t(label('creation'))"
                rules="required"
              >
                <b-form-group
                  :label="$t(label('creation'))"
                  label-for="creation"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-form-checkbox
                    id="creation"
                    v-model="permission.creation"
                    switch
                    inline
                    @input.native="changeSatusCreation($event)"
                    :disabled="!isSaveMode"
                  >
                    {{ isStatusPermissionCreation(permission.creation) }}
                  </b-form-checkbox>
                  <b-form-invalid-feedback
                    :state="errors.length > 0 ? false : null"
                  >
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12" md="3">
              <validation-provider
                #default="{ errors }"
                :name="$t(label('reading'))"
                rules="required"
              >
                <b-form-group
                  :label="$t(label('reading'))"
                  label-for="reading"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-form-checkbox
                    id="reading"
                    v-model="permission.reading"
                    switch
                    inline
                    @input.native="changeSatusReading($event)"
                    :disabled="!isSaveMode"
                  >
                    {{ isStatusPermissionReading(permission.reading) }}
                  </b-form-checkbox>
                  <b-form-invalid-feedback
                    :state="errors.length > 0 ? false : null"
                  >
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12" md="3">
              <validation-provider
                #default="{ errors }"
                :name="$t(label('edition'))"
                rules="required"
              >
                <b-form-group
                  :label="$t(label('edition'))"
                  label-for="edition"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-form-checkbox
                    id="edition"
                    v-model="permission.edition"
                    switch
                    inline
                    @input.native="changeSatusEdition($event)"
                    :disabled="!isSaveMode"
                  >
                    {{ isStatusPermissionEdition(permission.edition) }}
                  </b-form-checkbox>
                  <b-form-invalid-feedback
                    :state="errors.length > 0 ? false : null"
                  >
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="12" md="3">
              <validation-provider
                #default="{ errors }"
                :name="$t(label('elimination'))"
                rules="required"
              >
                <b-form-group
                  :label="$t(label('elimination'))"
                  label-for="elimination"
                  :state="errors.length > 0 ? false : null"
                >
                  <b-form-checkbox
                    id="elimination"
                    v-model="permission.elimination"
                    switch
                    inline
                    @input.native="changeSatusElimination($event)"
                    :disabled="!isSaveMode"
                  >
                    {{ isStatusPermissionElimination(permission.elimination) }}
                  </b-form-checkbox>
                  <b-form-invalid-feedback
                    :state="errors.length > 0 ? false : null"
                  >
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              class="d-flex justify-content-end align-items-center"
              cols="12"
            >
              <b-button
                v-if="option != 2"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                class="mr-1"
                :disabled="needsCompanySelection"
                @click.prevent="onSubmit"
              >
                {{ $t("save") }}
              </b-button>
              <b-button
                v-if="option == 1"
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="reset"
                variant="outline-secondary"
                @click="onCancel"
              >
                {{ $t("cancel") }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card-body>
  </b-card>
</template>

<script>
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BFormGroup,
  BForm,
  BFormCheckbox,
  BFormInvalidFeedback,
  BFormSelect,
  BAlert,
} from "bootstrap-vue";
import vSelect from "vue-select";
import router from "@/router";
import Ripple from "vue-ripple-directive";
import { required } from "@validations";

import { createOrEditPermission } from "@core/api/request/permissions";
import { getParams, showLoading, stopLoading } from "@core/utils/utils";
import { getCatalog } from "@core/api/request/catalogs";

const permission = {
  guid: null,
  name: null,
  idCatModule: null,
  idCatType: null,
  idCompany: null,
  reading: true,
  creation: true,
  edition: true,
  elimination: true,
};

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BFormGroup,
    BFormInput,
    BForm,
    BFormCheckbox,
    BFormInvalidFeedback,
    BFormSelect,
    BAlert,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data: () => ({
    isSaveMode: true,
    required,
    permission: Object.assign(permission),
    option: "",
    listCatModulesPermissions: [],
    listCatTypeProfiles: [],
    guidCatalogPermission: "cd25ec22-2250-40b7-a6be-356a6f162df7",
    guidCatalogTypeProfile: "953d6a85-82c4-4d02-abb2-13c9b5baa3ae",
    currentPage: 1,
    perPage: 999,
    idCompany: null,
  }),
  computed: {
    isClientProfile() {
      return Number(this.permission.idCatType) === 315;
    },
    needsCompanySelection() {
      return this.isClientProfile && !this.normalizeCompanyId(this.idCompany);
    },
  },
  created() {
    localize("es");
    this.initialize();
  },
  watch: {
    "$store.state.appConfig.activeCompanyGuid"(guid) {
      if (!guid) {
        this.idCompany = null;
      }
    },
    "$store.state.appConfig.idCompany"(idCompany) {
      this.idCompany = this.normalizeCompanyId(idCompany);
    },
  },
  mounted() {},
  methods: {
    initialize() {
      this.option = this.$route.params.option;

      if (this.option === undefined) {
        router.push({ name: "permissions" }).catch(() => {});
      }

      switch (this.option) {
        case 1:
          this.onCancel();
          this.loadCatData();
          this.syncActiveCompanyId();
          break;
        case 2:
        case 3:
          if (this.$route.params.guid === undefined) {
            router.push({ name: "permissions" }).catch(() => {});
          }
          this.isSaveMode = this.option !== 2;
          this.syncActiveCompanyId();
          this.loadCatData();
          this.getPermissions(this.$route.params.data);
          break;
      }
    },
    getPermissions(data) {
      let valueJson = JSON.parse(data);
      this.permission = Object.assign(this.permission, {
        guid: valueJson.guid || null,
        name: valueJson.name || null,
        idCatModule: valueJson.idCatModule || null,
        idCatType: valueJson.idCatType || null,
        idCompany: valueJson.idCompany || null,
        reading: valueJson.reading || false,
        creation: valueJson.creation || false,
        edition: valueJson.edition || false,
        elimination: valueJson.elimination || false,
      });
      console.log("--->", this.permission);
    },

    label(field) {
      return `pages.permissions.form.label.${field}`;
    },
    getTitle() {
      if (this.option === 1) return "register";
      else if (this.option === 2) return "show";
      else if (this.option === 3) return "edit";
    },
    onSubmit() {
      if (this.needsCompanySelection) {
        this.alertToast(
          "warning",
          this.$t("Alert"),
          this.$t("pages.permissions.form.selectClientCompany"),
        );
        return;
      }

      this.$refs.simpleRules.validate().then((success) => {
        if (success) {
          // showLoading();
          const data = {
            guid: this.permission.guid,
            name: this.permission.name,
            idCatModule: this.permission.idCatModule,
            idCatType: this.permission.idCatType,
            idCompany: this.isClientProfile
              ? this.normalizeCompanyId(this.idCompany)
              : null,
            reading: this.permission.reading,
            creation: this.permission.creation,
            edition: this.permission.edition,
            elimination: this.permission.elimination,
          };
          console.log("data", data);
          const idPermission = this.option === 1 ? null : this.permission.guid;
          createOrEditPermission(data, idPermission)
            .then(() => {
              stopLoading();
              this.alertToast("success", "Registro exitoso");
              this.goBack();
            })
            .catch((error) => {
              this.alertToast("danger", "Alerta error", error.message);
            })
            .finally(() => {
              stopLoading();
            });
        }
      });
    },

    normalizeCompanyId(value) {
      const idCompany = Number(value);
      return Number.isInteger(idCompany) && idCompany > 0 ? idCompany : null;
    },

    syncActiveCompanyId() {
      this.idCompany = this.normalizeCompanyId(
        this.$store.getters["appConfig/activeCompanyId"],
      );
    },

    goBack() {
      this.$refs.simpleRules.reset();
      router.back();
      this.onCancel();
    },

    onCancel() {
      Object.assign(this.permission, {
        guid: null,
        name: null,
        idCatModule: null,
        idCatType: null,
        idCompany: null,
        reading: true,
        creation: true,
        edition: true,
        elimination: true,
      });
      this.idCompany = null;
    },

    alertToast(type = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon: "CoffeeIcon",
      });
    },

    isStatusPermissionCreation(value) {
      return value ? "Si" : "No";
    },

    isStatusPermissionEdition(value) {
      return value ? "Si" : "No";
    },

    isStatusPermissionReading(value) {
      return value ? "Si" : "No";
    },

    isStatusPermissionElimination(value) {
      return value ? "Si" : "No";
    },

    changeSatusCreation(e) {
      e.stopPropagation();
      e.preventDefault();
    },

    changeSatusEdition(e) {
      e.stopPropagation();
      e.preventDefault();
    },

    changeSatusReading(e) {
      e.stopPropagation();
      e.preventDefault();
    },

    changeSatusElimination(e) {
      e.stopPropagation();
      e.preventDefault();
    },

    getCatalogPermissions() {
      showLoading();
      const param = this.chargeParams(this.guidCatalogPermission);
      getCatalog(param)
        .then((resp) => {
          stopLoading();
          this.listCatModulesPermissions = resp?.data?.listOfItems?.items || [];
        })
        .catch((error) => {
          stopLoading();
          this.alertToast("danger", error.message);
        });
    },

    async loadCatData() {
      showLoading();
      try {
        // Tipo de perfil
        // 953d6a85-82c4-4d02-abb2-13c9b5baa3ae

        // PERMISOS_MODULOS
        // cd25ec22-2250-40b7-a6be-356a6f162df7

        const paramPermission = this.chargeParams(this.guidCatalogPermission);
        const paramTypeProfile = this.chargeParams(this.guidCatalogTypeProfile);

        const [resultPermission, resultTypeProfile] = await Promise.all([
          getCatalog(paramPermission),
          getCatalog(paramTypeProfile),
        ]);

        this.listCatModulesPermissions =
          _.get(resultPermission, "data.listOfItems.items") || [];
        this.listCatTypeProfiles =
          _.get(resultTypeProfile, "data.listOfItems.items") || [];
        stopLoading();
      } catch (error) {
        stopLoading();
        this.alertToast(
          "danger",
          this.$t("Alert"),
          error.message,
          "AlertTriangleIcon",
        );
        console.error("Error loading data", error.message);
      }
    },

    chargeParams(guidCatalog) {
      const arrParams = [];

      arrParams.push(`itemsPage,${this.currentPage}`);

      arrParams.push(`itemsRows,${this.perPage}`);

      arrParams.push(`guidCatalog,${guidCatalog}`);

      return getParams(arrParams);
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
